import React, { isValidElement, Children, Fragment, useSyncExternalStore } from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '@/utils/cn';
import styles from './Tooltip.module.css';

const MOBILE_OR_COARSE_POINTER_QUERY = '(max-width: 767px), (pointer: coarse)';

function getMediaQueryList(): MediaQueryList | null {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(MOBILE_OR_COARSE_POINTER_QUERY)
    : null;
}

function subscribeToMobileOrCoarsePointer(onChange: () => void): () => void {
  const mediaQueryList = getMediaQueryList();
  if (!mediaQueryList) return () => undefined;

  mediaQueryList.addEventListener('change', onChange);
  return () => mediaQueryList.removeEventListener('change', onChange);
}

function getMobileOrCoarsePointerSnapshot(): boolean {
  return getMediaQueryList()?.matches ?? false;
}

function getMobileOrCoarsePointerServerSnapshot(): boolean {
  return false;
}

export interface TooltipProps {
  children: React.ReactNode;
  content: React.ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  delayDuration?: number;
  disabled?: boolean;
  contentClassName?: string;
}

export const Tooltip = React.forwardRef<HTMLButtonElement, TooltipProps>(
  (
    {
      children,
      content,
      side = 'top',
      align = 'center',
      delayDuration = 400,
      disabled = false,
      contentClassName,
      ...props
    },
    ref
  ) => {
    // WHY: tooltips are a hover affordance — on touch a tap would flash them (Radix opens on the
    // focus a tap gives). Disable them on the mobile shell even when the emulated viewport reports
    // a fine pointer; desktop keeps them. The subscription also handles a desktop preview resized
    // into the mobile shell without requiring a remount.
    const isMobileOrCoarsePointer = useSyncExternalStore(
      subscribeToMobileOrCoarsePointer,
      getMobileOrCoarsePointerSnapshot,
      getMobileOrCoarsePointerServerSnapshot
    );

    if (disabled || isMobileOrCoarsePointer) return <>{children}</>;

    if (!children) return null;

    const trigger = (() => {
      const count = Children.count(children);
      if (count > 1) {
        return <span className={styles.tooltipTriggerWrapper}>{children}</span>;
      }
      const onlyChild = Children.only(children);
      if (!isValidElement(onlyChild) || onlyChild.type === Fragment) {
        return <span className={styles.tooltipTriggerWrapper}>{children}</span>;
      }
      return onlyChild;
    })();

    return (
      <TooltipPrimitive.Provider>
        <TooltipPrimitive.Root delayDuration={delayDuration}>
          <TooltipPrimitive.Trigger asChild ref={ref} {...props}>
            {trigger}
          </TooltipPrimitive.Trigger>
          <TooltipPrimitive.Portal>
            <TooltipPrimitive.Content
              className={cn(styles.tooltipContent, contentClassName)}
              side={side}
              align={align}
              sideOffset={8}
            >
              {content}
              <TooltipPrimitive.Arrow className={styles.tooltipArrow} />
            </TooltipPrimitive.Content>
          </TooltipPrimitive.Portal>
        </TooltipPrimitive.Root>
      </TooltipPrimitive.Provider>
    );
  }
);

Tooltip.displayName = 'Tooltip';
export const TooltipProvider = TooltipPrimitive.Provider;
